// Kartlag «Infrastruktur (OSM)» — kommunikasjonsmaster/tårn, kraftverk/trafo og
// sykehus hentet fra OpenStreetMap via Overpass API, MED HØYDE der OSM har den.
//
// Dobbelt formål:
//   1) Kritisk infrastruktur i COP-en (supplerer J-5-lista, som er statisk).
//   2) Inngangsdata til antenne-/radardekning og siktelinjer: radiohorisonten
//      går som √h, så mast-/antennehøyde er avgjørende. `sites()` gir
//      `InfraSite[]` med `heightM` (over bakken) + `elevationM` (over havet).
//
// LIVE NETTVERK — piloten kan ha begrenset egress. Derfor:
//   · hard klient-timeout (AbortController), ikke bare Overpass' egen,
//   · 24 t cache i localStorage per grov-bbox (færre kall, virker offline etterpå),
//   · areal-tak (nekter for store spørringer i stedet for å henge),
//   · ærlig status (`status()`) som UI-et VISER — ingen falsk «tomt kart».
// Datakilden er byttbar: `fetchImpl` + `endpoint` injiseres, og laget kan mates
// direkte med `setSites()` fra en annen kilde (FKB, NKOM, manuell registrering).
import type { Map as MlMap, MapGeoJSONFeature } from 'maplibre-gl';
import ms from 'milsymbol';
import {
  featureCollection, overpassInfraQuery, parseOverpass, osmToInfraSites, bboxAreaDeg2,
  coarsenBBox, infraAntennaAmslM, INFRA_KINDS, INFRA_KIND_LABEL,
  type GeoBBox, type GeoFeature, type InfraKind, type InfraSite,
} from '@skade/contracts';

/** APP-6D landinstallasjon (symbolsett 20, venn) per kategori — verifiserte
 *  entitetskoder: 121203 telekomtårn · 121200 telekom-infrastruktur ·
 *  120500 elektrisk kraft · 120702 medisinsk behandlingsanlegg. */
const SIDC = (entity: string) => `1003200000${entity}0000`;

/** Farge + APP-6-symbol + lag-id per infrastruktur-kategori. */
const KIND: Record<InfraKind, { color: string; sidc: string; layer: string }> = {
  kommunikasjon: { color: '#7fbf7f', sidc: SIDC('121203'), layer: 'infra-kommunikasjon' },
  mast: { color: '#8b9b8b', sidc: SIDC('121200'), layer: 'infra-mast' },
  kraft: { color: '#e0a92e', sidc: SIDC('120500'), layer: 'infra-kraft' },
  sykehus: { color: '#a8b4a8', sidc: SIDC('120702'), layer: 'infra-sykehus' },
};

const SOURCE = 'infra-osm';
const CACHE_KEY = 'skade.infra.osm.cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 t — infrastruktur endres sakte
/** Maks bbox-areal i kvadratgrader. Over dette blir Overpass-svaret for stort
 *  og for langsomt for felt-bruk; vi sier det ærlig i stedet for å henge. */
const MAX_AREA_DEG2 = 1.5;
const DEFAULT_TIMEOUT_MS = 25_000;
const DEFAULT_ENDPOINT = 'https://overpass-api.de/api/interpreter';

/** Ærlig laststatus — UI-et viser denne ordrett, jf. UX_DESIGNSYSTEM §10. */
export type InfraStatus = 'tom' | 'laster' | 'ok' | 'cache' | 'for-stort' | 'nede';

export const INFRA_STATUS_TEXT: Record<InfraStatus, string> = {
  tom: 'Ingen elementer hentet ennå — trykk «Hent for utsnittet».',
  laster: 'Henter fra OpenStreetMap …',
  ok: 'Hentet fra OpenStreetMap (Overpass).',
  cache: 'Fra lokal buffer (offline-kopi).',
  'for-stort': 'Kartutsnittet er for stort — zoom inn og hent på nytt.',
  nede: 'Ingen kontakt med Overpass. Laget er tomt — dette er nettet, ikke data.',
};

export interface InfraOpts {
  /** Injiserbar fetch (test / annen transport). Default: global fetch. */
  fetchImpl?: typeof fetch;
  endpoint?: string;
  timeoutMs?: number;
  /** Injiserbar lagring (test). Default: localStorage. */
  store?: { getItem(k: string): string | null; setItem(k: string, v: string): void };
}

interface CacheEntry { bbox: GeoBBox; tsMs: number; sites: InfraSite[] }

export class InfraLag {
  private on: Record<InfraKind, boolean> = { kommunikasjon: false, mast: false, kraft: false, sykehus: false };
  private ready = false;
  private list: InfraSite[] = [];
  private state: InfraStatus = 'tom';
  private lastBbox: GeoBBox | null = null;
  private inflight: AbortController | null = null;
  private readonly fetchImpl?: typeof fetch;
  private readonly endpoint: string;
  private readonly timeoutMs: number;
  private readonly store?: InfraOpts['store'];

  /** Kalles ved hver tilstands-/dataendring (dock-knapp + tegnforklaring). */
  onStateChange?: () => void;

  constructor(private map: MlMap, opts: InfraOpts = {}) {
    this.fetchImpl = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined);
    this.endpoint = opts.endpoint ?? DEFAULT_ENDPOINT;
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.store = opts.store ?? (typeof localStorage !== 'undefined' ? localStorage : undefined);
    if (map.loaded?.()) this.init();
    else map.on?.('load', () => this.init());
  }

  // ── Kart ────────────────────────────────────────────────────────────────
  private registerSymbols() {
    for (const k of INFRA_KINDS) {
      const key = `infra-sym-${k}`;
      if (this.map.hasImage?.(key)) continue;
      try {
        const sym = new ms.Symbol(KIND[k].sidc, {
          size: 26, standard: 'APP6', monoColor: KIND[k].color, fill: false,
          outlineWidth: 3, outlineColor: '#0a0c0a',
        });
        const canvas = sym.asCanvas() as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        const { width, height } = canvas;
        this.map.addImage(key, { width, height, data: ctx.getImageData(0, 0, width, height).data }, { pixelRatio: 2 });
      } catch { /* milsymbol/canvas utilgjengelig — laget faller til tekstetikett */ }
    }
  }

  private init() {
    if (this.ready || this.map.getSource?.(SOURCE)) { this.ready = true; return; }
    this.map.addSource(SOURCE, { type: 'geojson', data: featureCollection([]) as never });
    this.registerSymbols();
    for (const k of INFRA_KINDS) {
      const { color, layer } = KIND[k];
      this.map.addLayer({
        id: layer, type: 'symbol', source: SOURCE,
        filter: ['==', ['get', 'kind'], k],
        layout: {
          visibility: 'none',
          'icon-image': `infra-sym-${k}`,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.45, 12, 0.7, 15, 0.9],
          'icon-allow-overlap': true,
          // Etiketten VISER høyden — det er selve poenget for dekningsmodulen.
          'text-field': ['get', 'label'], 'text-font': ['Open Sans Regular'],
          'text-size': 10, 'text-offset': [0, 1.4], 'text-anchor': 'top',
          'text-optional': true, 'text-max-width': 12,
        },
        paint: { 'text-color': color, 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.4 },
        minzoom: 8, // punkttettheten er meningsløs under dette
      });
    }
    this.ready = true;
  }

  private toFeatures(): GeoFeature[] {
    return this.list.map((s) => {
      const amsl = infraAntennaAmslM(s);
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [s.lon, s.lat] },
        properties: {
          id: s.id, name: s.name, kind: s.kind,
          label: s.heightM !== undefined ? `${s.name} · ${s.heightM} m` : s.name,
          heightM: s.heightM ?? null,
          elevationM: s.elevationM ?? null,
          amslM: amsl ?? null,
          towerType: s.towerType ?? '',
          operator: s.operator ?? '',
          source: s.source,
        },
      };
    });
  }

  private refresh() {
    const src = this.map.getSource?.(SOURCE) as { setData?: (d: unknown) => void } | undefined;
    src?.setData?.(featureCollection(this.toFeatures()));
  }

  // ── Av/på ───────────────────────────────────────────────────────────────
  setOn(kind: InfraKind, on: boolean): boolean {
    this.init();
    this.on[kind] = on;
    try { this.map.setLayoutProperty?.(KIND[kind].layer, 'visibility', on ? 'visible' : 'none'); } catch { /* laget ennå ikke lagt til */ }
    this.onStateChange?.();
    return on;
  }
  toggle(kind: InfraKind): boolean { return this.setOn(kind, !this.on[kind]); }
  isOn(kind: InfraKind): boolean { return this.on[kind]; }
  anyOn(): boolean { return INFRA_KINDS.some((k) => this.on[k]); }
  /** Slå på alle kategorier (idempotent). */
  enableAll(): boolean { for (const k of INFRA_KINDS) if (!this.on[k]) this.setOn(k, true); return true; }

  // ── Tellere + status (tegnforklaring) ───────────────────────────────────
  counts(): Record<InfraKind, number> {
    const c: Record<InfraKind, number> = { kommunikasjon: 0, mast: 0, kraft: 0, sykehus: 0 };
    for (const s of this.list) c[s.kind] += 1;
    return c;
  }
  visibleCount(): number {
    const c = this.counts();
    return INFRA_KINDS.reduce((n, k) => n + (this.on[k] ? c[k] : 0), 0);
  }
  /** Hvor mange av de hentede elementene har oppgitt høyde (dekningskvalitet). */
  withHeightCount(): number { return this.list.filter((s) => s.heightM !== undefined).length; }
  status(): InfraStatus { return this.state; }
  statusText(): string { return INFRA_STATUS_TEXT[this.state]; }
  /** Infrastruktur-elementene, til dekningsmodulen. */
  sites(): InfraSite[] { return [...this.list]; }
  kindLabel(k: InfraKind): string { return INFRA_KIND_LABEL[k]; }
  kindColor(k: InfraKind): string { return KIND[k].color; }

  /** APP-6-symbolet som PNG-data-URL — tegnforklaringen viser samme symbol som
   *  kartet. Tom streng uten canvas (node/test). */
  symbolDataUrl(k: InfraKind): string {
    try {
      const sym = new ms.Symbol(KIND[k].sidc, {
        size: 24, standard: 'APP6', monoColor: KIND[k].color, fill: false,
        outlineWidth: 2, outlineColor: '#0a0c0a',
      });
      const canvas = sym.asCanvas() as HTMLCanvasElement;
      return typeof canvas.toDataURL === 'function' ? canvas.toDataURL('image/png') : '';
    } catch { return ''; }
  }

  /** Mat laget direkte fra en annen kilde enn OSM (rent grensesnitt). */
  setSites(sites: InfraSite[], status: InfraStatus = 'ok') {
    this.init();
    this.list = sites;
    this.state = status;
    this.refresh();
    this.onStateChange?.();
  }

  // ── Henting ─────────────────────────────────────────────────────────────
  private viewportBBox(): GeoBBox | null {
    try {
      const b = this.map.getBounds?.();
      if (!b) return null;
      return coarsenBBox({ west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() });
    } catch { return null; }
  }

  private readCache(bbox: GeoBBox): InfraSite[] | null {
    try {
      const raw = this.store?.getItem(CACHE_KEY);
      if (!raw) return null;
      const e = JSON.parse(raw) as CacheEntry;
      if (!e || !Array.isArray(e.sites) || !e.bbox) return null;
      if (Date.now() - e.tsMs > CACHE_TTL_MS) return null;
      // Bufret utsnitt må DEKKE det vi ber om, ellers er svaret ufullstendig.
      const covers = e.bbox.west <= bbox.west && e.bbox.south <= bbox.south
        && e.bbox.east >= bbox.east && e.bbox.north >= bbox.north;
      return covers ? e.sites : null;
    } catch { return null; }
  }

  private writeCache(bbox: GeoBBox, sites: InfraSite[]) {
    try { this.store?.setItem(CACHE_KEY, JSON.stringify({ bbox, tsMs: Date.now(), sites } satisfies CacheEntry)); }
    catch { /* full/utilgjengelig — cache er en bonus, ikke et krav */ }
  }

  /**
   * Hent infrastruktur for gjeldende kartutsnitt. Bruker buffer når den dekker
   * utsnittet. Kaster ALDRI — statusen forteller hva som skjedde.
   * `force` hopper over bufferen (operatørens «Hent på nytt»).
   */
  async load(force = false): Promise<InfraStatus> {
    this.init();
    const bbox = this.viewportBBox();
    if (!bbox) { this.state = 'nede'; this.onStateChange?.(); return this.state; }

    if (bboxAreaDeg2(bbox) > MAX_AREA_DEG2) {
      this.state = 'for-stort';
      this.onStateChange?.();
      return this.state;
    }

    if (!force) {
      const cached = this.readCache(bbox);
      if (cached) {
        this.lastBbox = bbox;
        this.list = cached;
        this.state = 'cache';
        this.refresh();
        this.onStateChange?.();
        return this.state;
      }
    }

    if (!this.fetchImpl) { this.state = 'nede'; this.onStateChange?.(); return this.state; }

    this.inflight?.abort(); // bare siste utsnitt gjelder
    const ctrl = new AbortController();
    this.inflight = ctrl;
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs);
    this.state = 'laster';
    this.onStateChange?.();

    try {
      const body = new URLSearchParams({ data: overpassInfraQuery(bbox, Math.floor(this.timeoutMs / 1000) - 3) });
      const res = await this.fetchImpl(this.endpoint, { method: 'POST', body, signal: ctrl.signal });
      if (!res.ok) throw new Error(`Overpass HTTP ${res.status}`);
      const sites = osmToInfraSites(parseOverpass(await res.json()));
      this.lastBbox = bbox;
      this.list = sites;
      this.state = 'ok';
      this.writeCache(bbox, sites);
      this.refresh();
    } catch {
      // Nett nede / timeout / avbrutt: behold det vi allerede har (buffer),
      // men si ÆRLIG at hentingen feilet. Ingen dikting.
      this.state = this.list.length ? 'cache' : 'nede';
    } finally {
      clearTimeout(timer);
      if (this.inflight === ctrl) this.inflight = null;
      this.onStateChange?.();
    }
    return this.state;
  }

  /** Bbox som dataene faktisk gjelder for (til «utsnittet er flyttet»-hint). */
  loadedBBox(): GeoBBox | null { return this.lastBbox; }

  // ── Klikk-treff ─────────────────────────────────────────────────────────
  hitAt(point: { x: number; y: number }): MapGeoJSONFeature | null {
    const layers = INFRA_KINDS.map((k) => KIND[k].layer).filter((l) => this.map.getLayer?.(l));
    if (!layers.length) return null;
    try {
      const hits = this.map.queryRenderedFeatures([point.x, point.y] as never, { layers });
      return hits[0] ?? null;
    } catch { return null; }
  }
}
